import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Upload, Trash2, ExternalLink, Image as ImageIcon, 
  Code, LayoutGrid, AlertCircle 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getCleanUrl = (url) => {
  if (!url) return '';
  if (url.includes('http')) return url;
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const cleanBackend = backend.endsWith('/') ? backend.slice(0, -1) : backend;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${cleanBackend}${cleanUrl}`;
};

// Updated AD_TYPES to include all 4 slots we designed
const AD_TYPES = [
  { value: 'leaderboard', label: 'Leaderboard (Top Horizontal Banner)', hint: '720x90 size' },
  { value: 'sidebar', label: 'Sidebar Slot 1 (Top Square)', hint: 'Fixed square size' },
  { value: 'sidebar_v2', label: 'Sidebar Slot 2 (Middle Square)', hint: 'Fixed square size' },
  { value: 'interstitial', label: 'Sidebar Slot 3 (Bottom Square)', hint: 'Fixed square size' },
];

const AdManager = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [adMethod, setAdMethod] = useState('image'); // 'image' or 'script'
  const [form, setForm] = useState({ link: '', type: 'leaderboard', image: null, script: '' });

  useEffect(() => { fetchAds(); }, []);

  const fetchAds = async () => {
    try {
      const res = await axios.get(`${API}/ads`);
      setAds(res.data);
    } catch (err) { console.error('Failed to fetch ads:', err); }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm({ ...form, image: file });
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (adMethod === 'image' && !form.image) return toast.error('Please select an image');
    if (adMethod === 'script' && !form.script) return toast.error('Please paste the AdSense script');

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('type', form.type);
      
      if (adMethod === 'image') {
        formData.append('link', form.link);
        formData.append('image', form.image);
      } else {
        formData.append('script', form.script);
      }

      const token = localStorage.getItem('adminToken');
      
      await axios.post(`${API}/admin/ads`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'multipart/form-data' 
        },
      });

      setForm({ link: '', type: 'leaderboard', image: null, script: '' });
      setPreview(null);
      fetchAds();
      toast.success('Advertisement published successfully!');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Upload failed. Check admin login.');
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this advertisement?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/ads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ad removed');
      fetchAds();
    } catch (err) { 
      console.error('Delete error:', err);
      toast.error('Delete failed'); 
    }
  };

  return (
    <div className="space-y-8">
      {/* UPLOAD SECTION */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <h2 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
          <Upload className="h-6 w-6 text-red-600" /> ಪತ್ರಿಕೆ ಜಾಹೀರಾತು ಸೇರಿಸಿ (Add Ad)
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-bold">Select Slot</Label>
              <select 
                className="w-full p-3 border rounded-xl bg-gray-50 font-bold text-sm"
                value={form.type} 
                onChange={e => setForm({...form, type: e.target.value})}
              >
                {AD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div className="space-y-2">
              <Label className="font-bold">Ad Format</Label>
              <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
                <button 
                  type="button"
                  onClick={() => setAdMethod('image')}
                  className={`flex-1 py-2 rounded-lg font-bold text-xs transition-all ${adMethod === 'image' ? 'bg-white shadow text-red-600' : 'text-gray-500'}`}
                >
                  LOCAL IMAGE
                </button>
                <button 
                  type="button"
                  onClick={() => setAdMethod('script')}
                  className={`flex-1 py-2 rounded-lg font-bold text-xs transition-all ${adMethod === 'script' ? 'bg-white shadow text-red-600' : 'text-gray-500'}`}
                >
                  ADSENSE SCRIPT
                </button>
              </div>
            </div>
          </div>

          {adMethod === 'image' ? (
            <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="space-y-2">
                <Label className="font-bold">Target URL (Optional)</Label>
                <Input 
                  placeholder="https://facebook.com/kallinakoogu" 
                  value={form.link} 
                  onChange={e => setForm({...form, link: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <Label className="font-bold">Ad Image</Label>
                <Input type="file" onChange={handleImageChange} accept="image/*" className="py-2" />
                {preview && (
                  <div className="mt-4 p-2 border rounded-xl bg-gray-50 flex justify-center">
                    <img src={preview} className="h-40 object-contain rounded-lg" alt="preview" />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
              <Label className="font-bold text-red-600 flex items-center gap-2"><Code className="h-4 w-4" /> Paste Google AdSense Code</Label>
              <textarea 
                className="w-full p-4 bg-gray-900 text-green-400 font-mono text-xs h-40 rounded-xl border-2 border-gray-800"
                placeholder='<ins class="adsbygoogle" ... ></ins>'
                value={form.script}
                onChange={(e) => setForm({...form, script: e.target.value})}
              />
              <p className="text-[10px] text-gray-400 italic flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Note: Scripts will take priority over images.
              </p>
            </div>
          )}

          <Button className="w-full bg-red-600 hover:bg-black text-white font-black py-8 rounded-2xl shadow-lg shadow-red-100 transition-all" disabled={loading}>
            {loading ? 'Publishing...' : 'PUBLISH ADVERTISEMENT'}
          </Button>
        </form>
      </div>

      {/* LIST SECTION */}
      <div className="space-y-4">
        <h3 className="font-black text-gray-900 uppercase tracking-widest text-sm flex items-center gap-2">
          <LayoutGrid className="h-4 w-4" /> Current Active Ads
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {ads.length === 0 && <p className="text-sm text-gray-400 font-bold italic">No advertisements found in the system.</p>}
          {ads.map(ad => (
            <div key={ad.id} className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-20 h-14 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-center overflow-hidden">
                  {ad.imageUrl ? (
                    <img src={getCleanUrl(ad.imageUrl)} className="w-full h-full object-cover" alt="ad" />
                  ) : (
                    <Code className="h-6 w-6 text-gray-300" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-black uppercase text-red-600 tracking-tighter">{ad.type}</p>
                  <p className="text-xs font-bold text-gray-700 truncate">{ad.script ? 'AdSense Code Active' : 'Local Image Ad'}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(ad.id)} className="text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-full">
                <Trash2 className="h-5 w-5" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdManager;
